import type { Role } from "./types";

export type WorkspaceMembershipSummary = {
  roles: Role[];
  status: "active" | "suspended";
  clientAccess: { kind: "all" } | { kind: "selected"; companyIds: string[] };
};

const workspaceManagerRoles = new Set<Role>(["agency_admin", "account_manager"]);
const pieceNoteRoles = new Set<Role>(["agency_admin", "account_manager", "designer"]);

export function hasActiveMembership(memberships?: WorkspaceMembershipSummary[] | null): boolean {
  return Boolean(memberships?.some((membership) => membership.status === "active"));
}

export function canManageWorkspaceActions({
  companyId,
  memberships,
}: {
  companyId?: string;
  memberships?: WorkspaceMembershipSummary[] | null;
}): boolean {
  return Boolean(
    memberships?.some((membership) => {
      if (membership.status !== "active" || !membership.roles.some((role) => workspaceManagerRoles.has(role))) {
        return false;
      }

      if (!companyId) {
        return true;
      }

      return membership.roles.includes("agency_admin") || membership.clientAccess.kind === "all" || membership.clientAccess.companyIds.includes(companyId);
    }),
  );
}

export function canUseInternalPieceNotes({
  companyId,
  memberships,
}: {
  companyId?: string;
  memberships?: WorkspaceMembershipSummary[] | null;
}): boolean {
  return Boolean(
    memberships?.some((membership) => {
      if (membership.status !== "active" || !membership.roles.some((role) => pieceNoteRoles.has(role))) {
        return false;
      }
      // Deny until the company scope is known. This helper runs while the review query is
      // still loading, and enabling notes before the scope resolves fires a client-scoped
      // query the caller may not be authorized for.
      if (!companyId) return false;
      return membership.roles.includes("agency_admin")
        || membership.clientAccess.kind === "all"
        || membership.clientAccess.companyIds.includes(companyId);
    }),
  );
}
